import { Sparkles, Target, TrendingUp, BarChart3, Users, Megaphone } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  {
    name: "Campaign Strategy",
    icon: Target,
    components: [
      "Brand positioning alignment",
      "Campaign concept development",
      "Audience and media planning",
    ],
    outcome: "Campaigns that build brand while driving action.",
  },
  {
    name: "Creative Direction",
    icon: Sparkles,
    components: [
      "Creative brief development",
      "Multi-format asset planning",
      "Brand consistency management",
    ],
    outcome: "Creative that stands out and stays on-brand.",
  },
  {
    name: "Media Activation",
    icon: Megaphone,
    components: [
      "Cross-channel campaign execution",
      "Launch coordination and pacing",
      "Real-time optimization",
    ],
    outcome: "Seamless execution across all touchpoints.",
  },
  {
    name: "Audience Engagement",
    icon: Users,
    components: [
      "Experiential and interactive elements",
      "Social amplification strategies",
      "Community activation tactics",
    ],
    outcome: "Engagement that creates lasting brand connections.",
  },
  {
    name: "Performance Tracking",
    icon: BarChart3,
    components: [
      "Brand lift measurement",
      "Attribution and ROI analysis",
      "Competitive benchmarking",
    ],
    outcome: "Clear understanding of campaign impact.",
  },
  {
    name: "Amplification & Scale",
    icon: TrendingUp,
    components: [
      "PR and earned media integration",
      "Influencer and partner coordination",
      "Retargeting and remarketing",
    ],
    outcome: "Extended reach and compounding impact.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Brief & Discovery",
    description: "Understand business objectives, brand positioning, and campaign goals.",
  },
  {
    number: "02",
    title: "Concept Development",
    description: "Develop campaign concepts that balance brand building with performance.",
  },
  {
    number: "03",
    title: "Production & Prep",
    description: "Create assets and prepare media plans for launch.",
  },
  {
    number: "04",
    title: "Launch & Manage",
    description: "Execute campaigns with real-time monitoring and optimization.",
  },
  {
    number: "05",
    title: "Measure & Learn",
    description: "Analyze results and extract insights for future campaigns.",
  },
];

const technologies = [
  { category: "Display & Video", items: ["Google DV360", "YouTube", "Connected TV", "OOH Digital"] },
  { category: "Social", items: ["Meta", "TikTok", "LinkedIn", "Twitter/X"] },
  { category: "Measurement", items: ["Brand Lift Studies", "Kantar", "Nielsen", "Lucid"] },
  { category: "Creative", items: ["DCO Platforms", "Celtra", "Flashtalking", "Innovid"] },
];

const faqs = [
  {
    question: "How do you measure brand activation success?",
    answer: "We use a combination of brand lift studies, awareness surveys, and downstream performance metrics. The specific mix depends on campaign objectives.",
  },
  {
    question: "Can brand campaigns also drive immediate conversions?",
    answer: "Yes. We design campaigns that work across the funnel, building awareness while capturing existing demand. It's not either/or.",
  },
  {
    question: "What's the difference between brand activation and performance marketing?",
    answer: "Brand activation focuses on creating memorable experiences and associations. Performance marketing focuses on immediate actions. The best campaigns do both.",
  },
  {
    question: "How long should a brand campaign run?",
    answer: "It depends on objectives and budget. Sustained campaigns build more lasting impact than bursts, but we can design effective shorter-term activations.",
  },
  {
    question: "Do you handle creative production?",
    answer: "We provide strategic direction and can manage production partners, but we typically work with specialized creative agencies for production.",
  },
];

const qualification = {
  forYou: [
    "You're ready to invest in brand as a growth lever",
    "You understand that brand building takes time to show results",
    "You have a clear brand strategy and positioning",
    "You want to balance brand metrics with business outcomes",
  ],
  notForYou: [
    "You only care about last-click conversions",
    "You want results in weeks rather than months",
    "You don't have brand guidelines or positioning defined",
    "You're looking for the lowest cost-per-acquisition only",
  ],
};

const BrandActivations = () => {
  return (
    <SubServicePageLayout
      seoTitle="Brand Activation Services"
      seoDescription="Integrated brand campaigns that build awareness and drive action. Strategy, creative direction, and multi-channel activation for lasting impact."
      canonicalUrl="https://xperfomax.com/media-activations/brand-activations"
      category="media-activations"
      categoryLabel="Media Activations / Brand Activations"
      headline="Brand activation that resonates."
      subheadline="We create integrated brand campaigns that build awareness, drive consideration, and deliver measurable business impact."
      problemTitle="Why most brand campaigns fail to deliver."
      problems={[
        "Disconnect between brand creative and media strategy.",
        "No measurement framework to prove impact.",
        "Campaigns that build awareness but not preference.",
        "One-off activations without sustained presence.",
      ]}
      systemTitle="A performance system for brand activation."
      systemDescription="Strategy, creative, and media working together for measurable impact."
      services={services}
      processTitle="From concept to impact."
      processSteps={processSteps}
      technologies={technologies}
      techTitle="Platforms & Tools"
      techDescription="We work across premium channels and use sophisticated measurement tools."
      qualification={qualification}
      faqs={faqs}
      finalCtaTitle="Ready to activate your brand?"
      finalCtaDescription="Let's discuss how integrated brand campaigns can drive growth."
      finalCtaButtonText="Start a serious conversation"
    />
  );
};

export default BrandActivations;
